import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, NavLink, Route, Routes, useNavigate } from 'react-router-dom';
import { api, ApiError, retryRead, readRetryDelay } from './api.js';
import Dashboard from './pages/Dashboard.jsx';
import NewCampaign from './pages/NewCampaign.jsx';
import CampaignDetail from './pages/CampaignDetail.jsx';
import Leads from './pages/Leads.jsx';
import LeadDetail from './pages/LeadDetail.jsx';
import Costs from './pages/Costs.jsx';
import Login from './pages/Login.jsx';
import Administration from './pages/Administration.jsx';
import Settings from './pages/Settings.jsx';
import { AccessContext, PROFILE_LABELS, useAccess } from './access.js';
import { PageBoundary } from './components/PageBoundary.jsx';

export default function App() {
  const meQ = useQuery({
    queryKey: ['me'],
    queryFn: () => api.me(),
    retry: retryRead,
    retryDelay: readRetryDelay,
  });

  if (meQ.isLoading) {
    return (
      <FullScreen>
        {meQ.failureCount
          ? 'O Router está temporariamente indisponível. Tentando verificar sua sessão novamente… A campanha continua no servidor.'
          : 'Verificando sessão…'}
      </FullScreen>
    );
  }
  if (meQ.error instanceof ApiError && meQ.error.status === 401) {
    return <Login />;
  }
  if (meQ.error) {
    return (
      <FullScreen tone="error">
        <div className="space-y-4 max-w-lg px-4 text-center" role="alert">
          <p>Não foi possível verificar sua sessão.</p>
          <p className="text-sm text-slate-600">
            {meQ.error instanceof ApiError
              ? meQ.error.message
              : 'Verifique a conexão e tente novamente. Isso não cancela a campanha em andamento.'}
          </p>
          <button className="btn-primary" onClick={() => meQ.refetch()} disabled={meQ.isFetching}>
            Tentar novamente
          </button>
        </div>
      </FullScreen>
    );
  }

  return (
    <AccessContext.Provider value={meQ.data}>
      <div className="min-h-screen flex flex-col">
        <Header user={meQ.data} />
        <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-8">
          <PageBoundary>
            <Routes>
              <Route
                path="/"
                element={
                  meQ.data.tenant ? (
                    <Allowed permission="campaigns.read">
                      <Dashboard />
                    </Allowed>
                  ) : (
                    <Allowed permission="tenants.manage">
                      <Administration />
                    </Allowed>
                  )
                }
              />
              <Route
                path="/campaigns/new"
                element={
                  <Allowed permission="campaigns.write">
                    <NewCampaign />
                  </Allowed>
                }
              />
              <Route
                path="/campaigns/:id"
                element={
                  <Allowed permission="campaigns.read">
                    <CampaignDetail />
                  </Allowed>
                }
              />
              <Route
                path="/leads"
                element={
                  <Allowed permission="leads.read">
                    <Leads />
                  </Allowed>
                }
              />
              <Route
                path="/leads/:id"
                element={
                  <Allowed permission="leads.read">
                    <LeadDetail />
                  </Allowed>
                }
              />
              <Route
                path="/costs"
                element={
                  <Allowed permission="costs.read">
                    <Costs />
                  </Allowed>
                }
              />
              <Route
                path="/admin"
                element={
                  <Allowed permission="tenant.members.manage">
                    <Administration />
                  </Allowed>
                }
              />
              <Route path="*" element={<NotFound />} />
              <Route
                path="/settings"
                element={
                  <Allowed permission="tenant.settings.manage">
                    <Settings />
                  </Allowed>
                }
              />
            </Routes>
          </PageBoundary>
        </main>
      </div>
    </AccessContext.Provider>
  );
}

function Header({ user }) {
  const nav = useNavigate();
  const { can } = useAccess();
  const tenantsQ = useQuery({ queryKey: ['tenants'], queryFn: api.listTenants });
  const [switchError, setSwitchError] = useState('');
  return (
    <header className="border-b border-slate-200 bg-white">
      <div className="max-w-6xl mx-auto px-4 py-4 flex flex-wrap items-center gap-x-3 gap-y-3">
        <Link to="/" className="text-xl font-semibold text-brand-700">
          Nexify Lead
        </Link>
        <nav className="flex flex-wrap gap-x-3 gap-y-2 text-sm">
          {can('campaigns.read') && user?.tenant && <NavItem to="/">Dashboard</NavItem>}
          {can('leads.read') && user?.tenant && <NavItem to="/leads">Leads</NavItem>}
          {can('costs.read') && user?.tenant && <NavItem to="/costs">Custos</NavItem>}
          {can('tenant.members.manage') && <NavItem to="/admin">Administração</NavItem>}
          {can('tenant.settings.manage') && <NavItem to="/settings">Configurações</NavItem>}
        </nav>
        <div className="flex-1" />
        <span className="text-sm text-slate-500">
          {user?.email} · {PROFILE_LABELS[user?.profile] ?? 'Sem perfil atribuído'}
        </span>
        <label className="text-sm">
          <span className="sr-only">Tenant ativo</span>
          <select
            aria-label="Tenant ativo"
            className="input"
            value={user?.tenant?.id ?? ''}
            onChange={async (e) => {
              try {
                await api.selectTenant(e.target.value);
                window.location.assign('/');
              } catch (error) {
                setSwitchError(error.message);
              }
            }}
          >
            <option value="" disabled>
              Selecione um tenant
            </option>
            {(tenantsQ.data ?? [])
              .filter((t) => t.status === 'active')
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
          </select>
        </label>
        {switchError && (
          <span role="alert" className="text-red-600 text-sm">
            {switchError}
          </span>
        )}
        <button
          type="button"
          onClick={async () => {
            await api.logout();
            nav(0);
          }}
          className="text-sm text-slate-500 hover:text-slate-800"
        >
          Sair
        </button>
      </div>
    </header>
  );
}

function NavItem({ to, children }) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        isActive ? 'text-brand-700 font-medium' : 'text-slate-600 hover:text-slate-900'
      }
    >
      {children}
    </NavLink>
  );
}

function FullScreen({ children, tone = 'muted' }) {
  const cls = tone === 'error' ? 'text-red-600' : 'text-slate-500';
  return <div className={`min-h-screen grid place-items-center ${cls}`}>{children}</div>;
}

function NotFound() {
  return <div className="text-slate-500">Página não encontrada.</div>;
}

function Allowed({ permission, children }) {
  const { can } = useAccess();
  return can(permission) ? (
    children
  ) : (
    <p className="text-slate-600">
      Seu perfil não permite acessar esta área. Solicite acesso ao administrador.
    </p>
  );
}
